import React, { useEffect, useMemo, useState } from "react";
import {
  Workflow, Plus, Trash2, Star, Copy, ChevronDown, ChevronUp, X, ArrowRight,
  Globe, Download, Layers, Clock as ClockIcon, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const KIND_OPTIONS = [
  { value: "project", label: "Project lifecycle" },
  { value: "approval", label: "Approval pipeline" },
  { value: "quotation", label: "Quotation review" },
];

const blankStage = (n) => ({
  code: `stage_${n}`, label: `Stage ${n}`,
  default_assignee_role: "", sla_days: "", is_terminal: false,
  required_artifacts: [], auto_advance_when: "",
});

function StageRow({ stage, idx, onChange, onMove, onRemove, total }) {
  const set = (k) => (e) => onChange({ ...stage, [k]: e.target.value });
  return (
    <div className="border rounded-md p-2 bg-slate-50/40 space-y-1.5">
      <div className="flex items-center gap-1">
        <Badge variant="outline" className="text-[10px] font-mono">{idx + 1}</Badge>
        <Input value={stage.label} onChange={set("label")} placeholder="Display label"
               className="h-7 text-xs flex-1" />
        <Input value={stage.code} onChange={set("code")} placeholder="code"
               className="h-7 text-xs w-32 font-mono" />
        <button onClick={() => onMove(idx, -1)} disabled={idx === 0}
                className="text-slate-400 hover:text-blue-600 disabled:text-slate-200">
          <ChevronUp className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onMove(idx, 1)} disabled={idx === total - 1}
                className="text-slate-400 hover:text-blue-600 disabled:text-slate-200">
          <ChevronDown className="w-3.5 h-3.5" />
        </button>
        <button onClick={onRemove} className="text-slate-300 hover:text-rose-500">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-1 text-xs">
        <Input value={stage.default_assignee_role} onChange={set("default_assignee_role")}
               placeholder="default role (e.g. tech_lead)"
               className="h-7 text-[11px] font-mono" />
        <Input type="number" value={stage.sla_days} onChange={set("sla_days")}
               placeholder="SLA days" className="h-7 text-[11px]" />
        <label className="flex items-center gap-1 text-[10px] text-slate-600">
          <input type="checkbox" checked={!!stage.is_terminal}
                 onChange={(e) => onChange({ ...stage, is_terminal: e.target.checked })} />
          terminal stage (no exits)
        </label>
      </div>
    </div>
  );
}

function TransitionMatrix({ stages, parallel_groups = {}, transitions, onChange }) {
  // Both stage codes AND parallel-group codes are valid sources / destinations.
  // We render group rows/columns with a ↬ prefix so users can wire them in.
  const stageCodes = stages.map((s) => s.code);
  const groupCodes = Object.keys(parallel_groups || {});
  const allCodes = [...stageCodes, ...groupCodes];

  const toggle = (from, to) => {
    const cur = new Set(transitions[from] || []);
    if (cur.has(to)) cur.delete(to);
    else cur.add(to);
    onChange({ ...transitions, [from]: Array.from(cur) });
  };
  if (allCodes.length === 0) {
    return <p className="text-xs italic text-slate-400">Add stages first.</p>;
  }
  const labelFor = (code) => groupCodes.includes(code) ? `↬ ${code}` : code;
  return (
    <div className="overflow-auto">
      <table className="text-[10px] font-mono">
        <thead>
          <tr>
            <th className="text-left text-slate-500 px-1">from \ to →</th>
            {allCodes.map((c) => (
              <th key={c}
                  className={`px-1 text-left rotate-45 origin-left whitespace-nowrap py-3 ${
                    groupCodes.includes(c) ? "text-violet-600 font-semibold" : "text-slate-500"
                  }`}>
                {labelFor(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {allCodes.map((from) => (
            <tr key={from}>
              <td className={`px-1 ${
                groupCodes.includes(from) ? "text-violet-700 font-semibold" : "text-slate-700"
              }`}>
                {labelFor(from)}
              </td>
              {allCodes.map((to) => {
                const checked = (transitions[from] || []).includes(to);
                const disabled = from === to;
                return (
                  <td key={`${from}-${to}`} className="px-1 text-center">
                    <input type="checkbox" disabled={disabled} checked={checked}
                           onChange={() => toggle(from, to)} />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ParallelGroupsEditor({ stages, parallel_groups, onChange }) {
  const [draftName, setDraftName] = useState("");
  const stageCodes = stages.map((s) => s.code);
  const groups = parallel_groups || {};

  const addGroup = () => {
    const name = draftName.trim();
    if (!name) return;
    if (groups[name] || stageCodes.includes(name)) {
      alert(`Code "${name}" already exists as a stage or group.`);
      return;
    }
    onChange({ ...groups, [name]: { stages: [], completion_rule: "all" } });
    setDraftName("");
  };
  const removeGroup = (name) => {
    const next = { ...groups };
    delete next[name];
    onChange(next);
  };
  const toggleMember = (gname, code) => {
    const g = groups[gname];
    const set = new Set(g.stages || []);
    if (set.has(code)) set.delete(code);
    else set.add(code);
    onChange({ ...groups, [gname]: { ...g, stages: Array.from(set) } });
  };
  const setRule = (gname, rule) => {
    const g = groups[gname];
    onChange({ ...groups, [gname]: { ...g, completion_rule: rule } });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs text-slate-500 flex items-center gap-1">
          <Layers className="w-3 h-3 text-violet-600" /> Parallel groups
        </Label>
        <span className="text-[10px] text-slate-400 italic">
          stages that run together; advance is gated on completion rule
        </span>
      </div>
      <div className="space-y-2">
        {Object.entries(groups).map(([name, g]) => (
          <div key={name} className="border border-violet-200 bg-violet-50/40 rounded p-2 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Badge variant="outline" className="text-[10px] font-mono text-violet-700 border-violet-300">
                ↬ {name}
              </Badge>
              <select value={g.completion_rule || "all"}
                      onChange={(e) => setRule(name, e.target.value)}
                      className="text-[10px] px-1 py-0.5 border rounded bg-white">
                <option value="all">all-of (every member must complete)</option>
                <option value="any">any-of (one is enough)</option>
              </select>
              <button onClick={() => removeGroup(name)}
                      className="ml-auto text-slate-300 hover:text-rose-500">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1">
              {stageCodes.length === 0 && (
                <span className="text-[10px] italic text-slate-400">Add stages first.</span>
              )}
              {stageCodes.map((c) => {
                const isMember = (g.stages || []).includes(c);
                return (
                  <button key={c} onClick={() => toggleMember(name, c)}
                          className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full border ${
                            isMember
                              ? "bg-violet-600 text-white border-violet-600"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-violet-50"
                          }`}>
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1 mt-2">
        <Input value={draftName} onChange={(e) => setDraftName(e.target.value)}
               placeholder="new group code (e.g. review_phase)"
               className="h-7 text-xs flex-1" />
        <Button size="sm" variant="outline" onClick={addGroup} disabled={!draftName.trim()}>
          <Plus className="w-3 h-3 mr-1" /> group
        </Button>
      </div>
    </div>
  );
}

function TemplateDialog({ open, onOpenChange, initial, onSaved }) {
  const blank = () => ({
    name: "", kind: "project", description: "",
    is_default: false, is_active: true, is_public: false,
    stages: [
      { ...blankStage(1), label: "Draft" },
      { ...blankStage(2), label: "Review" },
      { ...blankStage(3), label: "Done", is_terminal: true },
    ],
    transitions: { stage_1: ["stage_2"], stage_2: ["stage_3"] },
    parallel_groups: {},
    start_stage: "stage_1",
  });
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const d = initial.definition || {};
      setForm({
        name: initial.name, kind: initial.kind,
        description: initial.description || "",
        is_default: initial.is_default, is_active: initial.is_active,
        is_public: initial.is_public || false,
        stages: d.stages || [],
        transitions: d.transitions || {},
        parallel_groups: d.parallel_groups || {},
        start_stage: d.start_stage || (d.stages?.[0]?.code) || "",
      });
    } else {
      setForm(blank());
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const updateStage = (i, next) => {
    const stages = [...form.stages];
    const oldCode = stages[i].code;
    stages[i] = next;
    const transitions = { ...form.transitions };
    // Rename in transitions if code changed
    if (oldCode !== next.code) {
      if (transitions[oldCode]) {
        transitions[next.code] = transitions[oldCode];
        delete transitions[oldCode];
      }
      Object.keys(transitions).forEach((k) => {
        transitions[k] = transitions[k].map((d) => (d === oldCode ? next.code : d));
      });
    }
    setForm({
      ...form, stages, transitions,
      start_stage: form.start_stage === oldCode ? next.code : form.start_stage,
    });
  };
  const moveStage = (i, dir) => {
    const stages = [...form.stages];
    const j = i + dir;
    if (j < 0 || j >= stages.length) return;
    [stages[i], stages[j]] = [stages[j], stages[i]];
    setForm({ ...form, stages });
  };
  const removeStage = (i) => {
    const code = form.stages[i].code;
    const stages = form.stages.filter((_, idx) => idx !== i);
    const transitions = { ...form.transitions };
    delete transitions[code];
    Object.keys(transitions).forEach((k) => {
      transitions[k] = transitions[k].filter((d) => d !== code);
    });
    setForm({
      ...form, stages, transitions,
      start_stage: form.start_stage === code ? (stages[0]?.code || "") : form.start_stage,
    });
  };
  const addStage = () => {
    const n = form.stages.length + 1;
    setForm({ ...form, stages: [...form.stages, blankStage(n)] });
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (form.stages.length === 0) { toast.error("Need at least one stage"); return; }
    if (!form.start_stage) { toast.error("Pick a start stage"); return; }
    const codes = new Set(form.stages.map((s) => s.code));
    if (codes.size !== form.stages.length) {
      toast.error("Stage codes must be unique"); return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        kind: form.kind,
        description: form.description.trim() || null,
        is_default: form.is_default,
        is_active: form.is_active,
        is_public: form.is_public,
        definition: {
          stages: form.stages.map((s) => ({
            code: s.code,
            label: s.label || s.code,
            default_assignee_role: s.default_assignee_role || null,
            sla_days: s.sla_days === "" ? null : Number(s.sla_days),
            is_terminal: !!s.is_terminal,
            required_artifacts: s.required_artifacts || [],
            auto_advance_when: s.auto_advance_when || null,
          })),
          transitions: form.transitions,
          parallel_groups: form.parallel_groups,
          start_stage: form.start_stage,
        },
      };
      if (initial?.id) await v2Api.workflows.patchTemplate(initial.id, payload);
      else await v2Api.workflows.createTemplate(payload);
      toast.success("Template saved");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit workflow template" : "New workflow template"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-slate-500">Name</Label>
              <Input value={form.name} onChange={set("name")} />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Kind</Label>
              <select value={form.kind} onChange={set("kind")}
                      className="w-full px-2 py-1 border rounded-md bg-white text-sm">
                {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Description</Label>
            <Input value={form.description} onChange={set("description")} />
          </div>
          <div className="flex gap-3 text-xs flex-wrap">
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={form.is_default}
                     onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />
              Default for {form.kind}
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={form.is_active}
                     onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              Active
            </label>
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={form.is_public}
                     onChange={(e) => setForm({ ...form, is_public: e.target.checked })} />
              <Globe className="w-3 h-3 inline" /> Publish to marketplace
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-slate-500">Stages (drag with ↑↓)</Label>
              <Button size="sm" variant="outline" onClick={addStage}>
                <Plus className="w-3 h-3 mr-1" /> stage
              </Button>
            </div>
            <div className="space-y-2">
              {form.stages.map((s, i) => (
                <StageRow
                  key={i} stage={s} idx={i} total={form.stages.length}
                  onChange={(next) => updateStage(i, next)}
                  onMove={moveStage}
                  onRemove={() => removeStage(i)}
                />
              ))}
            </div>
          </div>

          <ParallelGroupsEditor
            stages={form.stages}
            parallel_groups={form.parallel_groups}
            onChange={(parallel_groups) => setForm({ ...form, parallel_groups })}
          />

          <div>
            <Label className="text-xs text-slate-500">Start stage</Label>
            <select value={form.start_stage}
                    onChange={(e) => setForm({ ...form, start_stage: e.target.value })}
                    className="w-full px-2 py-1 border rounded-md bg-white text-sm">
              <option value="">— pick start stage —</option>
              <optgroup label="Stages">
                {form.stages.map((s) => (
                  <option key={s.code} value={s.code}>{s.label} ({s.code})</option>
                ))}
              </optgroup>
              {Object.keys(form.parallel_groups || {}).length > 0 && (
                <optgroup label="Parallel groups">
                  {Object.keys(form.parallel_groups).map((g) => (
                    <option key={g} value={g}>↬ {g}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div>
            <Label className="text-xs text-slate-500">Transitions matrix</Label>
            <p className="text-[10px] text-slate-400 mb-1">
              Tick which destination stages each source stage can move into.
              Self-cells are disabled. Empty rows = terminal-by-omission.
            </p>
            <TransitionMatrix
              stages={form.stages}
              parallel_groups={form.parallel_groups}
              transitions={form.transitions}
              onChange={(transitions) => setForm({ ...form, transitions })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>
            {busy ? "Saving…" : initial?.id ? "Save" : "Create template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarketplaceCard({ t, onCloned }) {
  const [busy, setBusy] = useState(false);
  const stages = (t.definition || {}).stages || [];
  const groups = (t.definition || {}).parallel_groups || {};
  const cloneNow = async () => {
    setBusy(true);
    try {
      await v2Api.workflows.cloneFromMarketplace(t.id, {});
      toast.success("Cloned into your workspace");
      onCloned?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Clone failed");
    } finally { setBusy(false); }
  };
  return (
    <Card className="border-violet-200">
      <CardContent className="py-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-slate-800">{t.name}</p>
              <Badge variant="outline" className="text-[10px]">{t.kind}</Badge>
              <Badge className="bg-violet-100 text-violet-700 border-violet-200 text-[10px]">
                <Globe className="w-3 h-3 inline mr-0.5" /> public
              </Badge>
              {t.cloned_to_my_workspace_count > 0 && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                  cloned {t.cloned_to_my_workspace_count}× by me
                </Badge>
              )}
            </div>
            {t.description && (
              <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>
            )}
            {t.source_workspace_name && (
              <p className="text-[10px] text-slate-400 mt-0.5">
                Published by {t.source_workspace_name}
              </p>
            )}
          </div>
          <Button size="sm" onClick={cloneNow} disabled={busy}>
            <Download className="w-3.5 h-3.5 mr-1" /> {busy ? "Cloning…" : "Clone"}
          </Button>
        </div>
        <div className="flex items-center gap-1 flex-wrap text-[10px] font-mono">
          {stages.map((s, i) => (
            <React.Fragment key={s.code}>
              <span className={`px-1.5 py-0.5 rounded border ${
                s.code === t.definition?.start_stage
                  ? "bg-blue-50 text-blue-700 border-blue-200"
                  : s.is_terminal
                  ? "bg-rose-50 text-rose-700 border-rose-200"
                  : "bg-slate-50 text-slate-700 border-slate-200"
              }`}>
                {s.label}
                {s.sla_days != null && <span className="opacity-60 ml-1">· {s.sla_days}d</span>}
              </span>
              {i < stages.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300" />}
            </React.Fragment>
          ))}
        </div>
        {Object.keys(groups).length > 0 && (
          <p className="text-[10px] text-violet-600">
            <Layers className="w-3 h-3 inline mr-0.5" /> Parallel groups:{" "}
            {Object.entries(groups).map(([n, g]) => (
              <span key={n} className="mr-2">
                ↬ {n} ({g.completion_rule}-of: {(g.stages || []).join(", ")})
              </span>
            ))}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function WorkflowsPage() {
  const [view, setView] = useState("mine"); // mine | marketplace
  const [items, setItems] = useState([]);
  const [marketplace, setMarketplace] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [slaRunning, setSlaRunning] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [mine, mkt] = await Promise.all([
        v2Api.workflows.listTemplates(),
        v2Api.workflows.marketplace().catch(() => []),
      ]);
      setItems(mine || []);
      setMarketplace(mkt || []);
    } catch { toast.error("Could not load templates"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const runSla = async () => {
    setSlaRunning(true);
    try {
      const r = await v2Api.workflows.runSla();
      toast.success(
        `${r.breaches_found} breaches · ${r.notifications_emitted} notifications sent · ${r.notifications_skipped_already_notified} already notified`,
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "SLA scan failed");
    } finally { setSlaRunning(false); }
  };

  const remove = async (t) => {
    if (!await confirm(`Delete template "${t.name}"?`, { title: "Delete template", confirmLabel: "Delete" })) return;
    try { await v2Api.workflows.deleteTemplate(t.id); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail ?? "Delete failed"); }
  };
  const setDefault = async (t) => {
    try { await v2Api.workflows.patchTemplate(t.id, { is_default: true }); refresh(); }
    catch { toast.error("Update failed"); }
  };
  const clone = (t) => {
    setEditing({
      ...t, id: undefined, name: `${t.name} (copy)`, is_default: false,
    });
    setOpen(true);
  };

  const filtered = useMemo(() => {
    const source = view === "marketplace" ? marketplace : items;
    return filter === "all" ? source : source.filter((t) => t.kind === filter);
  }, [items, marketplace, view, filter]);

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Workflow className="w-5 h-5 text-blue-600" /> Workflow Templates
          </h1>
          <p className="text-sm text-slate-500">
            Reusable pipelines for project / approval / quotation flows.
            Author once, apply per-entity, advance through stages with
            allowed-transition guards. The default-template flag picks the
            workspace-wide default for each kind.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={runSla} disabled={slaRunning}>
            <ClockIcon className="w-4 h-4 mr-1" /> {slaRunning ? "Scanning…" : "Run SLA scan"}
          </Button>
          <Button onClick={() => { setEditing(null); setOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" /> New template
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b">
        <button onClick={() => setView("mine")}
                className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${
                  view === "mine"
                    ? "text-blue-700 border-blue-600"
                    : "text-slate-500 border-transparent hover:text-slate-700"
                }`}>
          My templates ({items.length})
        </button>
        <button onClick={() => setView("marketplace")}
                className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px flex items-center gap-1 ${
                  view === "marketplace"
                    ? "text-violet-700 border-violet-600"
                    : "text-slate-500 border-transparent hover:text-slate-700"
                }`}>
          <Globe className="w-3.5 h-3.5" /> Marketplace ({marketplace.length})
        </button>
      </div>

      <div className="flex gap-1 text-xs">
        {[{ id: "all", label: "All" }, ...KIND_OPTIONS.map((k) => ({ id: k.value, label: k.label }))].map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)}
                  className={`px-3 py-1 rounded-full border ${
                    filter === f.id
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200"
                  }`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && filtered.length === 0 && (
        <Card><CardContent className="py-10 text-center text-slate-400">
          {view === "marketplace"
            ? "No public templates available yet. Be the first to publish one — toggle 'Publish to marketplace' when authoring."
            : "No templates yet. Click 'New template' to author your first pipeline."}
        </CardContent></Card>
      )}

      <div className="space-y-2">
        {view === "marketplace" && filtered.map((t) => (
          <MarketplaceCard key={t.id} t={t} onCloned={refresh} />
        ))}
        {view === "mine" && filtered.map((t) => {
          const stages = (t.definition || {}).stages || [];
          const trans = (t.definition || {}).transitions || {};
          return (
            <Card key={t.id} className={t.is_active ? "" : "opacity-60"}>
              <CardContent className="py-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-800">{t.name}</p>
                      <Badge variant="outline" className="text-[10px]">{t.kind}</Badge>
                      {t.is_default && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">
                          <Star className="w-3 h-3 inline mr-0.5" /> default
                        </Badge>
                      )}
                      <Badge className={t.is_active
                        ? "bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]"
                        : "bg-slate-100 text-slate-500 border-slate-200 text-[10px]"}>
                        {t.is_active ? "active" : "inactive"}
                      </Badge>
                    </div>
                    {t.description && (
                      <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!t.is_default && (
                      <button onClick={() => setDefault(t)}
                              className="text-[10px] text-amber-600 hover:underline">
                        set default
                      </button>
                    )}
                    <button onClick={() => clone(t)} title="Duplicate"
                            className="text-slate-300 hover:text-blue-500">
                      <Copy className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setEditing(t); setOpen(true); }}
                            className="text-[10px] text-blue-600 hover:underline">
                      edit
                    </button>
                    <button onClick={() => remove(t)} className="text-slate-300 hover:text-rose-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-1 flex-wrap text-[10px] font-mono">
                  {stages.map((s, i) => {
                    const isStart = s.code === t.definition?.start_stage;
                    return (
                      <React.Fragment key={s.code}>
                        <span className={`px-1.5 py-0.5 rounded border ${
                          isStart ? "bg-blue-50 text-blue-700 border-blue-200"
                                  : s.is_terminal ? "bg-rose-50 text-rose-700 border-rose-200"
                                  : "bg-slate-50 text-slate-700 border-slate-200"
                        }`}>
                          {s.label}
                          {s.default_assignee_role && (
                            <span className="opacity-60 ml-1">· {s.default_assignee_role}</span>
                          )}
                          {s.sla_days != null && (
                            <span className="opacity-60 ml-1">· {s.sla_days}d</span>
                          )}
                        </span>
                        {i < stages.length - 1 && (
                          <ArrowRight className="w-3 h-3 text-slate-300" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
                {Object.keys((t.definition || {}).parallel_groups || {}).length > 0 && (
                  <div className="text-[10px] text-violet-700 flex items-center gap-1 flex-wrap">
                    <Layers className="w-3 h-3" />
                    {Object.entries((t.definition || {}).parallel_groups || {}).map(([n, g]) => (
                      <Badge key={n} className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] font-mono">
                        ↬ {n} ({g.completion_rule}): {(g.stages || []).join(" + ")}
                      </Badge>
                    ))}
                  </div>
                )}
                {t.is_public && (
                  <p className="text-[10px] text-violet-600 flex items-center gap-1">
                    <Globe className="w-3 h-3" /> Published to marketplace
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <TemplateDialog open={open} onOpenChange={setOpen}
                      initial={editing} onSaved={refresh} />
    </div>
  );
}
